/**
 * Contract Resource Hooks
 *
 * Type-safe hooks for contract-related API operations.
 * Includes support for nested resources (roster, labor categories, role assignments).
 *
 * Note: Named with "Query" suffix to avoid conflicts with app-level useContract context hook.
 */

import type { paths } from '@access-cms/api-types';
import { ResourceType } from '@access-cms/shared-constants';

import { type ApiFileResponse, type ApiRequestError, apiRequestFile } from '../../client';
import { queryKeys } from '../../queryKeys';
import type { QueryParamRecord } from '../../types';
import { useApiAction, useApiCreate, useApiDelete, useApiUpdate, useApiUpsert } from '../useApiMutation';
import { useApiGet, useApiList, useApiSearch } from '../useApiQuery';

// =============================================================================
// Type Aliases
// =============================================================================

type ContractListResponse = paths['/api/v1/contracts']['get']['responses']['200']['content']['application/json'];
type ContractResponse = paths['/api/v1/contracts/{id}']['get']['responses']['200']['content']['application/json'];
type ContractCreateBody = paths['/api/v1/contracts']['post']['requestBody']['content']['application/json'];
type ContractUpdateBody = NonNullable<
  paths['/api/v1/contracts/{id}']['patch']['requestBody']
>['content']['application/json'];

type Contract = NonNullable<ContractResponse['data']>;
type ContractListFilters = paths['/api/v1/contracts']['get']['parameters']['query'];
type ContractSearchBody = NonNullable<
  paths['/api/v1/contracts/search']['post']['requestBody']
>['content']['application/json'];
type ContractSearchResponse =
  paths['/api/v1/contracts/search']['post']['responses']['200']['content']['application/json'];
type DelegateFederalApproverResolveBody = NonNullable<
  paths['/api/v1/contracts/{contractId}/delegate-federal-approver/resolve']['post']['requestBody']
>['content']['application/json'];
type DelegateFederalApproverResolveResponse =
  paths['/api/v1/contracts/{contractId}/delegate-federal-approver/resolve']['post']['responses']['200']['content']['application/json'];
type DelegateFederalApproverUpsertBody = NonNullable<
  paths['/api/v1/contracts/{contractId}/delegate-federal-approver']['put']['requestBody']
>['content']['application/json'];

// =============================================================================
// Contract Query Hooks
// =============================================================================

interface UseContractsQueryOptions {
  enabled?: boolean;
  filters?: ContractListFilters;
}

/**
 * Fetch a paginated list of contracts
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useContractsQuery({
 *   filters: { status: 'ACTIVE', limit: 20 }
 * });
 * ```
 */
export function useContractsQuery(options?: UseContractsQueryOptions) {
  const { filters, enabled } = options ?? {};

  return useApiList('/api/v1/contracts', {
    enabled,
    queryKey: queryKeys[ResourceType.CONTRACTS].list(filters as QueryParamRecord),
    queryParams: filters,
  });
}

interface UseContractQueryOptions {
  enabled?: boolean;
}

/**
 * Fetch a single contract by ID
 *
 * @example
 * ```tsx
 * const { data: contract, isLoading } = useContractQuery(contractId);
 * ```
 */
export function useContractQuery(id: string, options?: UseContractQueryOptions) {
  return useApiGet(
    '/api/v1/contracts/{id}',
    { id },
    {
      enabled: options?.enabled ?? !!id,
      queryKey: queryKeys[ResourceType.CONTRACTS].detail(id),
    },
  );
}

interface UseContractsSearchQueryOptions {
  enabled?: boolean;
}

/**
 * Advanced contract search with multi-field filters (POST body).
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useContractsSearchQuery({
 *   keyword: 'ACME',
 *   contractStatus: 'ACTIVE',
 * });
 * ```
 */
export function useContractsSearchQuery(body: ContractSearchBody, options?: UseContractsSearchQueryOptions) {
  return useApiSearch('/api/v1/contracts/search', body, {
    enabled: options?.enabled,
    queryKey: queryKeys[ResourceType.CONTRACTS].search(body as QueryParamRecord),
  });
}

// =============================================================================
// Contract Mutation Hooks
// =============================================================================

interface UseCreateContractOptions {
  onError?: (error: Error) => void;
  onSuccess?: (response: ContractResponse) => void;
}

/**
 * Create a new contract
 */
export function useCreateContract(options?: UseCreateContractOptions) {
  return useApiCreate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/contracts',
  });
}

interface UseUpdateContractOptions {
  onError?: (error: Error) => void;
  onSuccess?: (response: ContractResponse) => void;
}

/**
 * Update an existing contract
 */
export function useUpdateContract(options?: UseUpdateContractOptions) {
  return useApiUpdate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/contracts/{id}',
  });
}

interface UseDeleteContractOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Delete a contract
 */
export function useDeleteContract(options?: UseDeleteContractOptions) {
  return useApiDelete({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    path: '/api/v1/contracts/{id}',
  });
}

// =============================================================================
// Roster Hooks
// =============================================================================

type RosterListResponse =
  paths['/api/v1/contracts/{contractId}/roster']['get']['responses']['200']['content']['application/json'];
type RosterMemberResponse =
  paths['/api/v1/contracts/{contractId}/roster/{userId}']['get']['responses']['200']['content']['application/json'];
type RosterFilters = paths['/api/v1/contracts/{contractId}/roster']['get']['parameters']['query'];

interface UseRosterQueryOptions {
  enabled?: boolean;
  filters?: RosterFilters;
}

/**
 * Fetch roster members for a contract
 *
 * @example
 * ```tsx
 * const { data: roster } = useContractRosterQuery(contractId, {
 *   filters: { rosterStatus: 'ACTIVE' }
 * });
 * ```
 */
export function useContractRosterQuery(contractId: string, options?: UseRosterQueryOptions) {
  const { filters, enabled } = options ?? {};

  return useApiList('/api/v1/contracts/{contractId}/roster', {
    enabled: enabled ?? !!contractId,
    pathParams: { contractId },
    queryKey: queryKeys[ResourceType.CONTRACTS].nested(contractId).roster.list(filters as QueryParamRecord),
    queryParams: filters,
  });
}

/**
 * Download the contract roster as a spreadsheet-safe CSV.
 *
 * The file is generated server-side so untrusted values cannot be evaluated as
 * spreadsheet formulas [ACP-2142]. Unlike {@link useContractRosterQuery} this
 * returns every member matching the filters, not a single page.
 *
 * @returns The CSV blob and the filename the server suggested.
 */
export async function exportContractRoster(contractId: string, filters?: QueryParamRecord): Promise<ApiFileResponse> {
  return apiRequestFile({
    path: '/api/v1/contracts/{contractId}/roster/export',
    pathParams: { contractId },
    queryParams: filters,
  });
}

/**
 * Fetch a single roster member
 */
export function useContractRosterMemberQuery(contractId: string, userId: string, options?: { enabled?: boolean }) {
  return useApiGet(
    '/api/v1/contracts/{contractId}/roster/{userId}',
    { contractId, userId },
    {
      enabled: options?.enabled ?? !!(contractId && userId),
      queryKey: queryKeys[ResourceType.CONTRACTS].nested(contractId).roster.detail(userId),
    },
  );
}

type RosterCreateBody =
  paths['/api/v1/contracts/{contractId}/roster']['post']['requestBody']['content']['application/json'];

interface UseAddRosterMemberOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Add a member to a contract roster
 */
export function useAddContractRosterMember(contractId: string, options?: UseAddRosterMemberOptions) {
  return useApiCreate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    parentId: contractId,
    path: '/api/v1/contracts/{contractId}/roster',
    pathParams: { contractId },
  });
}

type RosterUpdateBody = NonNullable<
  paths['/api/v1/contracts/{contractId}/roster/{userId}']['patch']['requestBody']
>['content']['application/json'];

interface UseUpdateRosterMemberOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Update a roster member
 */
export function useUpdateContractRosterMember(contractId: string, options?: UseUpdateRosterMemberOptions) {
  return useApiUpdate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    parentId: contractId,
    path: '/api/v1/contracts/{contractId}/roster/{userId}',
    pathParams: { contractId },
  });
}

/** Record an explicit roster certification and invalidate contract data. */
export function useCertifyContractRoster(contractId: string) {
  return useApiAction<'/api/v1/contracts/{contractId}/roster/certify'>({
    parentId: contractId,
    path: '/api/v1/contracts/{contractId}/roster/certify',
    pathParams: { contractId },
  });
}

interface UseRemoveRosterMemberOptions {
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}

/**
 * Remove a member from a contract roster
 */
export function useRemoveContractRosterMember(contractId: string, options?: UseRemoveRosterMemberOptions) {
  return useApiDelete({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    parentId: contractId,
    path: '/api/v1/contracts/{contractId}/roster/{userId}',
    pathParams: { contractId },
  });
}

type RosterInvitationResendPath = '/api/v1/contracts/{contractId}/roster/{userId}/resend-invitation';
export type RosterInvitationResendResponse =
  paths[RosterInvitationResendPath]['post']['responses']['200']['content']['application/json'];

interface UseResendRosterInvitationOptions {
  onError?: (error: ApiRequestError) => void;
  onSuccess?: (response: RosterInvitationResendResponse) => void;
}

/**
 * Resend a roster member invitation and refresh the affected roster caches.
 */
export function useResendContractRosterInvitation(contractId: string, options?: UseResendRosterInvitationOptions) {
  return useApiAction({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    parentId: contractId,
    path: '/api/v1/contracts/{contractId}/roster/{userId}/resend-invitation',
    pathParams: { contractId },
  });
}

// =============================================================================
// Labor Categories Hooks
// =============================================================================

type LaborCategoryListResponse =
  paths['/api/v1/contracts/{contractId}/labor-categories']['get']['responses']['200']['content']['application/json'];

interface UseLaborCategoriesQueryOptions {
  enabled?: boolean;
}

/**
 * Fetch labor categories for a contract
 */
export function useContractLaborCategoriesQuery(contractId: string, options?: UseLaborCategoriesQueryOptions) {
  return useApiList('/api/v1/contracts/{contractId}/labor-categories', {
    enabled: options?.enabled ?? !!contractId,
    pathParams: { contractId },
    queryKey: queryKeys[ResourceType.CONTRACTS].nested(contractId).laborCategories.list(),
  });
}

type LaborCategoryCreateBody =
  paths['/api/v1/contracts/{contractId}/labor-categories']['post']['requestBody']['content']['application/json'];

/**
 * Create a labor category for a contract
 */
export function useCreateContractLaborCategory(
  contractId: string,
  options?: { onError?: (error: Error) => void; onSuccess?: () => void },
) {
  return useApiCreate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    parentId: contractId,
    path: '/api/v1/contracts/{contractId}/labor-categories',
    pathParams: { contractId },
  });
}

// =============================================================================
// Role Assignments Hooks
// =============================================================================

type RoleAssignmentListResponse =
  paths['/api/v1/contracts/{contractId}/role-assignments']['get']['responses']['200']['content']['application/json'];
type RoleAssignmentFilters = paths['/api/v1/contracts/{contractId}/role-assignments']['get']['parameters']['query'];

interface UseRoleAssignmentsQueryOptions {
  enabled?: boolean;
  filters?: RoleAssignmentFilters;
}

/**
 * Fetch role assignments for a contract
 *
 * @example
 * ```tsx
 * const { data } = useContractRoleAssignmentsQuery(contractId, {
 *   filters: { roleType: 'ROSTER_OWNER', active: true }
 * });
 * ```
 */
export function useContractRoleAssignmentsQuery(contractId: string, options?: UseRoleAssignmentsQueryOptions) {
  const { filters, enabled } = options ?? {};

  return useApiList('/api/v1/contracts/{contractId}/role-assignments', {
    enabled: enabled ?? !!contractId,
    pathParams: { contractId },
    queryKey: queryKeys[ResourceType.CONTRACTS].nested(contractId).roleAssignments.list(filters as QueryParamRecord),
    queryParams: filters,
  });
}

/** Resolve a Delegate Federal Approver candidate by normalized CMS email. */
export function useResolveDelegateFederalApprover(contractId: string) {
  return useApiCreate({
    parentId: contractId,
    path: '/api/v1/contracts/{contractId}/delegate-federal-approver/resolve',
    pathParams: { contractId },
  });
}

/** Atomically assign or replace the contract's Delegate Federal Approver. */
export function useUpsertDelegateFederalApprover(contractId: string) {
  return useApiUpsert({
    parentId: contractId,
    path: '/api/v1/contracts/{contractId}/delegate-federal-approver',
    pathParams: { contractId },
  });
}

type RoleAssignmentCreateBody =
  paths['/api/v1/contracts/{contractId}/role-assignments']['post']['requestBody']['content']['application/json'];

/**
 * Create a role assignment for a contract
 */
export function useCreateContractRoleAssignment(
  contractId: string,
  options?: { onError?: (error: Error) => void; onSuccess?: () => void },
) {
  return useApiCreate({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    parentId: contractId,
    path: '/api/v1/contracts/{contractId}/role-assignments',
    pathParams: { contractId },
  });
}

/**
 * Delete a role assignment
 */
export function useDeleteContractRoleAssignment(
  contractId: string,
  options?: { onError?: (error: Error) => void; onSuccess?: () => void },
) {
  return useApiDelete({
    onError: options?.onError,
    onSuccess: options?.onSuccess,
    parentId: contractId,
    path: '/api/v1/contracts/{contractId}/role-assignments/{assignmentId}',
    pathParams: { contractId },
  });
}

// =============================================================================
// Exports
// =============================================================================

export type {
  Contract,
  ContractCreateBody,
  ContractListFilters,
  ContractListResponse,
  ContractResponse,
  ContractSearchBody,
  ContractSearchResponse,
  ContractUpdateBody,
  DelegateFederalApproverResolveBody,
  DelegateFederalApproverResolveResponse,
  DelegateFederalApproverUpsertBody,
  LaborCategoryCreateBody,
  LaborCategoryListResponse,
  RoleAssignmentCreateBody,
  RoleAssignmentFilters,
  RoleAssignmentListResponse,
  RosterCreateBody,
  RosterFilters,
  RosterListResponse,
  RosterMemberResponse,
  RosterUpdateBody,
};
